/**
 * Date Range Presets
 *
 * Preset date range configurations for quick selection.
 * Supports Dutch locale formatting.
 */

export interface DateRange {
  start: Date;
  end: Date;
}

export interface DateRangePreset {
  id: string;
  label: string;
  labelNl: string;
  getRange: () => DateRange;
}

/**
 * Get the start of today
 */
function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Get the end of today
 */
function endOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

/**
 * Add days to a date
 */
function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

/**
 * Get the start of the current month
 */
function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);
}

/**
 * Get the end of the current month
 */
function endOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);
}

/**
 * Predefined date range presets
 */
export const DATE_RANGE_PRESETS: DateRangePreset[] = [
  {
    id: 'last7days',
    label: 'Last 7 days',
    labelNl: 'Afgelopen 7 dagen',
    getRange: () => ({
      start: startOfDay(addDays(new Date(), -6)),
      end: endOfDay(new Date()),
    }),
  },
  {
    id: 'last30days',
    label: 'Last 30 days',
    labelNl: 'Afgelopen 30 dagen',
    getRange: () => ({
      start: startOfDay(addDays(new Date(), -29)),
      end: endOfDay(new Date()),
    }),
  },
  {
    id: 'thisMonth',
    label: 'This month',
    labelNl: 'Deze maand',
    getRange: () => ({
      start: startOfMonth(new Date()),
      end: endOfDay(new Date()),
    }),
  },
  {
    id: 'lastMonth',
    label: 'Last month',
    labelNl: 'Vorige maand',
    getRange: () => {
      const now = new Date();
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return {
        start: startOfMonth(lastMonth),
        end: endOfMonth(lastMonth),
      };
    },
  },
  {
    id: 'last90days',
    label: 'Last 90 days',
    labelNl: 'Afgelopen 90 dagen',
    getRange: () => ({
      start: startOfDay(addDays(new Date(), -89)),
      end: endOfDay(new Date()),
    }),
  },
  {
    id: 'thisYear',
    label: 'This year',
    labelNl: 'Dit jaar',
    getRange: () => ({
      start: new Date(new Date().getFullYear(), 0, 1, 0, 0, 0, 0),
      end: endOfDay(new Date()),
    }),
  },
];

/**
 * Format a date range for display
 */
export function formatDateRange(range: DateRange | null, locale: string = 'nl-NL'): string {
  if (!range) return '';

  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  };

  const formatter = new Intl.DateTimeFormat(locale, options);
  const startStr = formatter.format(range.start);
  const endStr = formatter.format(range.end);

  // Check if same day
  if (
    range.start.getFullYear() === range.end.getFullYear() &&
    range.start.getMonth() === range.end.getMonth() &&
    range.start.getDate() === range.end.getDate()
  ) {
    return startStr;
  }

  return `${startStr} - ${endStr}`;
}

/**
 * Format a date for input type="date"
 */
export function formatDateForInput(date: Date | null): string {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Parse date from input type="date"
 */
export function parseDateFromInput(value: string): Date | null {
  if (!value) return null;
  const date = new Date(value + 'T00:00:00');
  return isNaN(date.getTime()) ? null : date;
}
